import { useEffect, useState } from 'react';
import { Container, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { BsPersonCircle } from 'react-icons/bs';
import { LuMenu } from 'react-icons/lu';
import { MdLogout } from 'react-icons/md';
import { RxCross2 } from 'react-icons/rx';
import { Link, useLocation } from 'react-router-dom';
import SFCLogoImg, { ReactComponent as SFCLogoLarge } from '../../assets/icons/sfc-logo-large.svg';
import CartIconWithCount from '../../components/cartIcon/CartIconWithCount';
import { useCartContext } from '../../context/CartContext';
import { getCurrentUser, isAuthenticated, logoutUser } from '../../services/helper';
import { hasDashAccess } from '../../utils/consts';
import { LocationSetter } from '../maps/LocationSetter';
import { NavItem, SFCNavItems } from './NavBarItems';
import './SFCNavbar.css';

const calcBorderRadius = (idx: number, length: number) => {
  if (idx === 0 && length === 1) return '8px 0 8px 8px';
  if (idx === length - 1) return '0 0 8px 8px';
  if (idx === 0) return '8px 0 0 0';
  return '0';
};

export const SFCNavbar = () => {
  const authenticated = isAuthenticated();
  const currentUser = getCurrentUser();
  const cartContext = useCartContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const showDashLink =
    authenticated && hasDashAccess(currentUser?.user_type_id!);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <Navbar
      collapseOnSelect
      expand="xl"
      expanded={menuOpen}
      onToggle={setMenuOpen}
      className="sfc-navbar py-0"
      style={{
        position: 'sticky',
        top: 0,
        boxShadow: '0 4px 4px 0 rgba(0, 0, 0, .25)',
        zIndex: 101,
        backgroundColor: 'white'
      }}
    >
      <Container className="flex-xl-column">
        <Navbar.Collapse
          id="responsive-navbar-nav"
          className="header-container d-none d-xl-flex"
        >
          <div className="navbar-emptyspace"></div>
          <div className="logo-container">
            <Navbar.Brand href="/">
              <SFCLogoLarge className="w-100" />
            </Navbar.Brand>
          </div>
          <div className="navbar-container">
            <div className="navbar-buttons">
              <div className="navbar-buttons__location">
                <LocationSetter variant="desktop" />
              </div>
              <Link
                to="/cart"
                className="fw-bold text-uppercase text-truncate text-dark me-3 ms-3"
              >
                <CartIconWithCount />
              </Link>
              {authenticated ? (
                <NavDropdown
                  title={<BsPersonCircle size={24} />}
                  align="end"
                >
                  {showDashLink && (
                    <NavDropdown.Item
                      href="/admin"
                      className="fw-semibold"
                      style={{ borderRadius: calcBorderRadius(0, 3) }}
                    >
                      The Dash
                    </NavDropdown.Item>
                  )}
                  <NavDropdown.Item
                    href="/user/profile"
                    className="fw-semibold"
                    style={{
                      borderRadius: calcBorderRadius(showDashLink ? 1 : 0, showDashLink ? 3 : 2)
                    }}
                  >
                    My Account
                  </NavDropdown.Item>
                  <hr style={{ width: '85%', margin: '10px auto' }} />
                  <NavDropdown.Item
                    href="#profile"
                    className="d-flex justify-content-between fw-semibold"
                    style={{
                      borderRadius: calcBorderRadius(showDashLink ? 2 : 1, showDashLink ? 3 : 2)
                    }}
                    onClick={() => logoutUser()}
                  >
                    Logout
                    <MdLogout size={20} />
                  </NavDropdown.Item>
                </NavDropdown>
              ) : (
                <Link to="/users/login" className="sfc-login-link">
                  Login
                </Link>
              )}
            </div>
          </div>
        </Navbar.Collapse>

        {/* Mobile header bar */}
        <div className="d-flex d-xl-none align-items-center justify-content-between w-100 py-2">
          <Navbar.Brand href="/">
            <img
              src={SFCLogoImg}
              style={{ maxWidth: 220, maxHeight: 60 }}
              alt="Sticky Fingers Cooking"
            />
          </Navbar.Brand>
          <Navbar.Toggle
            aria-controls="responsive-navbar-nav"
            className="me-3 border-0 p-0"
          >
            <div style={{ position: 'relative', width: 34, height: 34 }}>
              <LuMenu
                size={30}
                color="#b4c634"
                style={{
                  position: 'absolute',
                  top: 2,
                  left: 2,
                  transition: 'transform 0.3s ease, opacity 0.3s ease',
                  opacity: menuOpen ? 0 : 1,
                  transform: menuOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                }}
              />
              <RxCross2
                size={34}
                color="#b4c634"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transition: 'transform 0.3s ease, opacity 0.3s ease',
                  opacity: menuOpen ? 1 : 0,
                  transform: menuOpen ? 'rotate(0deg)' : 'rotate(-90deg)'
                }}
              />
            </div>
          </Navbar.Toggle>
        </div>

        <Navbar.Collapse id="responsive-navbar-nav" className="sfc-navbar-menu">
          <Nav variant="pills">
            {showDashLink && (
              <Nav.Link href="/admin" className="sfc-dash-link d-xl-none fw-bold text-uppercase">
                The Dash
              </Nav.Link>
            )}
            {authenticated && (
              <Nav.Link href="/user/profile" className="d-xl-none fw-bold text-uppercase">
                My Account
              </Nav.Link>
            )}
            {!authenticated && (
              <Nav.Link href="/users/login" className="d-xl-none fw-bold text-uppercase">
                Login
              </Nav.Link>
            )}
            {SFCNavItems.map((navItem: NavItem, index) =>
              navItem.subItems?.length ? (
                <NavDropdown
                  key={index}
                  title={navItem.title}
                  id="collapsible-nav-dropdown"
                  className={`fw-bold text-uppercase${navItem.hiddenOnMobile ? ' d-none d-xl-block' : ''}`}
                  align="end"
                >
                  {navItem.subItems
                    .filter((f) => !f.hidden)
                    .map((subItem: NavItem, idx: number, array: NavItem[]) => (
                      <NavDropdown.Item
                        key={idx}
                        href={subItem.url}
                        className="text-capitalize fw-semibold"
                        style={{ borderRadius: calcBorderRadius(idx, array.length) }}
                      >
                        {subItem.title}
                      </NavDropdown.Item>
                    ))}
                </NavDropdown>
              ) : (
                <Nav.Link
                  key={index}
                  href={navItem.url}
                  className={`fw-bold text-uppercase text-truncate${navItem.hiddenOnMobile ? ' d-none d-xl-block' : ''}`}
                >
                  {navItem.title}
                </Nav.Link>
              )
            )}
          </Nav>

          <div className="sfc-navbar-menu-footer d-xl-none border-top d-flex align-items-center justify-content-between mt-3 px-3 pb-2 pt-3">
            <div className="sfc-navbar-menu-footer__location">
              <LocationSetter variant="mobile" />
            </div>
            <div className="sfc-navbar-menu-footer__actions d-flex align-items-center gap-3">
              <Link
                to="/cart"
                className="fw-bold text-uppercase text-dark"
                onClick={closeMobileMenu}
              >
                <CartIconWithCount />
              </Link>
              {authenticated && (
                <button
                  type="button"
                  className="sfc-mobile-logout-btn btn btn-link p-0"
                  onClick={() => logoutUser()}
                  aria-label="Logout"
                >
                  <MdLogout size={24} />
                </button>
              )}
            </div>
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};
